import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle, ShieldCheck, Info, FileText, AlertTriangle, Power, Clock, MapPin,
  Star, MessageSquare, Pencil, Plus, CalendarPlus,
} from 'lucide-react';
import {
  useMentorData, buildExperiences, buildFeedback, ratingStats,
  fmtTime, UPCOMING_STATUSES, todayLocal,
} from './mentorData';
import {
  MentorWorkspace, MentorLoading, NoMentorProfile, ExperienceRow, SectionHeading,
  CompleteExperienceDialog, DeclarationDialog, RatingStars,
} from './MentorComponents';
import { ImpactAndRecognition } from './MentorRecognition';

export default function MentorHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { loading, profile, sessions, bookings, eligibility, declarationInfo, refresh } = useMentorData();
  const [completing, setCompleting] = useState(null);
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    api.get('/mentor/nudges').then(r => setNudges(r.data || [])).catch(() => {});
    api.get('/mentor/impact').then(r => setImpact(r.data)).catch(() => {});
  }, []);
  const handleComplete = (e) => { if (e.source === 'session') navigate(e.link); else setCompleting(e); };

  if (loading) return <MentorLoading />;
  if (!profile) return <NoMentorProfile />;

  const st = profile.approval_status;
  const isApproved = st === 'approved';
  const isSuspended = st === 'suspended';
  const isDeactivated = st === 'deactivated';
  const today = todayLocal();

  const experiences = buildExperiences(sessions, bookings);
  const upcoming = experiences.filter(e => e.date >= today && UPCOMING_STATUSES.includes(e.status));
  const awaitingCompletion = experiences.filter(e => e.date < today && UPCOMING_STATUSES.includes(e.status));

  const feedback = buildFeedback(bookings);
  const { total: ratingTotal, distribution, average } = ratingStats(feedback);

  const needsDeclaration = isApproved && declarationInfo && !declarationInfo.has_accepted;
  const suitabilityPending = (eligibility?.outstanding_requirements || []).some(r => r.toLowerCase().includes('suitability'));
  const attentionCount = awaitingCompletion.length + (needsDeclaration ? 1 : 0) + nudges.length;

  const statusText = !isApproved ? st
    : profile.has_pending_changes ? 'Approved · Update pending review'
    : eligibility?.eligibility === 'eligible' ? 'Approved mentor'
    : eligibility?.needs_declaration && !eligibility?.needs_suitability ? 'Approved · Declaration required'
    : eligibility?.needs_suitability && !eligibility?.needs_declaration ? 'Approved · School verification pending'
    : 'Approved';

  const firstName = (user?.full_name || 'Mentor')
    .replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '')
    .split(' ')[0];
  const nextExp = upcoming[0];
  const laterExps = upcoming.slice(1, 4);
  const onlineSlots = profile.availability_config || [];
  const offlineCaps = profile.offline_capabilities || [];

  return (
    <MentorWorkspace testId="mentor-home">
      {/* Welcome + impact */}
      <section className="mb-10" data-testid="welcome-impact">
        <p className="text-xs font-semibold text-[#F97D6A] uppercase tracking-widest mb-2" data-testid="mentor-status">{statusText}</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="mentor-title">
          Hello, {firstName}
        </h1>
        <p className="text-sm sm:text-base text-[#6B7280] mt-2">{profile.profession_title}{profile.company_name ? ` at ${profile.company_name}` : ''}</p>

      </section>

      {/* Your Impact & Recognition */}
      {(isApproved || isSuspended) && (
        <section className="mb-10" data-testid="your-impact">
          <ImpactAndRecognition impact={impact} />
        </section>
      )}

      {/* What needs you today */}
      {isApproved && (
        <section className="mb-10" data-testid="needs-attention">
          <SectionHeading title="What needs you today" />
          {attentionCount === 0 && (
            <p className="text-sm text-[#6B7280]" data-testid="needs-attention-clear">
              You&apos;re all clear — nothing is waiting on you right now.
            </p>
          )}
          <div className="space-y-2.5">
            {needsDeclaration && (
              <div className="rounded-2xl border border-[#FBD9D2] bg-[#FFF7F5] p-4 flex items-center gap-3" data-testid="attention-declaration">
                <ShieldCheck className="h-5 w-5 text-[#F97D6A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1F2937]">Safeguarding declaration renewal</p>
                  <p className="text-sm text-[#6B7280] mt-0.5">Review and accept version {declarationInfo?.declaration_version} to continue mentoring.</p>
                </div>
                <Button size="sm" onClick={() => setShowDeclaration(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold shrink-0" data-testid="review-accept-btn">
                  Review & Accept
                </Button>
              </div>
            )}
            {nudges.map(n => (
              <div key={n.career_slug} className="rounded-2xl border border-[#FBD9D2] bg-[#FFF7F5] p-4 flex items-center gap-3"
                data-testid={`attention-nudge-${n.career_slug}`}>
                <CalendarPlus className="h-5 w-5 text-[#F97D6A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1F2937]">{n.career_name} — no times published</p>
                  <p className="text-sm text-[#6B7280] mt-0.5">
                    Your school is waiting on a time for this career. Add your availability or a workplace visit date.
                  </p>
                </div>
                <Button size="sm" onClick={() => navigate('/mentor/profile')}
                  className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold shrink-0"
                  data-testid={`publish-time-btn-${n.career_slug}`}>
                  Publish a time
                </Button>
              </div>
            ))}
            {awaitingCompletion.map(e => (
              <div key={e.id} className="rounded-2xl border border-[#FBD9D2] bg-[#FFF7F5] p-4 flex items-center gap-3" data-testid={`attention-${e.id}`}>
                <AlertCircle className="h-5 w-5 text-[#F97D6A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#1F2937] truncate">{e.career} — confirm completion</p>
                    <Badge className={`border-0 text-[9px] shrink-0 ${e.offline ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`} data-testid={`attention-mode-${e.id}`}>
                      {e.offline ? 'Workplace Visit' : 'Online Session'}
                    </Badge>
                  </div>
                  <p className="text-sm text-[#6B7280] mt-0.5">{formatDate(e.date)}{e.childName ? ` · ${e.childName}` : ` · ${e.studentCount} student${e.studentCount === 1 ? '' : 's'}`}</p>
                </div>
                <Button size="sm" onClick={() => handleComplete(e)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold shrink-0" data-testid={`attention-complete-${e.id}`}>
                  Complete
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Status notices */}
      {isSuspended && (
        <div className="rounded-2xl bg-orange-50 px-5 py-4 mb-6 flex items-start gap-3" data-testid="suspension-notice">
          <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800">Mentor profile suspended</p>
            <p className="text-xs text-orange-600 mt-0.5">Your approval has been suspended by your Career Coordinator. Mentoring actions are restricted.</p>
            {profile.suspension_reason && <p className="text-xs text-orange-700 mt-1 italic">Reason: "{profile.suspension_reason}"</p>}
          </div>
        </div>
      )}
      {isDeactivated && (
        <div className="rounded-2xl bg-slate-50 px-5 py-4 mb-6 flex items-start gap-3" data-testid="deactivation-notice">
          <Power className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Mentor profile deactivated</p>
            <p className="text-xs text-slate-500 mt-0.5">Your mentor profile has been deactivated. Historical information is available below.</p>
          </div>
        </div>
      )}
      {st === 'pending' && (
        <div className="rounded-2xl bg-amber-50 px-5 py-4 mb-6 flex items-center gap-3" data-testid="pending-notice">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">Your application is under review by your Career Coordinator.</p>
        </div>
      )}
      {st === 'rejected' && (
        <div className="rounded-2xl bg-red-50 px-5 py-4 mb-6" data-testid="rejected-notice">
          <p className="text-sm text-red-800">Your application needs changes. Please review, update and resubmit.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/mentor/onboarding')} className="mt-2 rounded-full text-xs h-8 border-red-200 text-red-600">Edit & Resubmit</Button>
        </div>
      )}
      {isApproved && profile.has_pending_changes && (
        <div className="rounded-2xl bg-blue-50 px-5 py-4 mb-6 flex items-start gap-3" data-testid="pending-changes-notice">
          <FileText className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Profile update pending review</p>
            <p className="text-xs text-blue-600 mt-0.5">Your proposed changes are with your Career Coordinator. Your current profile stays active.</p>
          </div>
        </div>
      )}
      {isApproved && suitabilityPending && (
        <div className="rounded-2xl bg-slate-50 px-5 py-4 mb-6 flex items-start gap-3" data-testid="suitability-info-notice">
          <Info className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-700">School verification pending</p>
            <p className="text-xs text-slate-500 mt-0.5">School suitability verification is pending — your coordinator will complete this step.</p>
          </div>
        </div>
      )}

      {/* Upcoming experiences */}
      {(isApproved || isSuspended) && (
        <section className="mb-10" data-testid="upcoming-experiences">
          <SectionHeading title="Upcoming experiences" action="View all" onAction={() => navigate('/mentor/experiences')} actionTestId="view-all-experiences" />
          {!nextExp ? (
            <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-8 text-center" data-testid="upcoming-empty">
              <p className="text-sm text-[#9CA3AF]">No upcoming experiences scheduled yet.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              <ExperienceRow exp={nextExp} featured onComplete={handleComplete} />
              {laterExps.map(e => <ExperienceRow key={e.id} exp={e} onComplete={handleComplete} />)}
            </div>
          )}
        </section>
      )}

      {/* Parent feedback */}
      <section className="mb-10" data-testid="ratings-feedback">
        <SectionHeading title="Parent feedback" />
        {ratingTotal === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-8 text-center" data-testid="feedback-empty">
            <MessageSquare className="h-7 w-7 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-[#9CA3AF]">No parent feedback yet. Ratings appear here once parents review a completed experience.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-5 gap-5">
            <div className="md:col-span-2 rounded-2xl border border-[#EFF0F2] p-5" data-testid="rating-summary">
              <div className="flex items-end gap-2">
                <p className="text-4xl font-bold text-[#1F2937] font-['Outfit'] leading-none">{average.toFixed(1)}</p>
                <span className="text-sm text-[#9CA3AF] mb-1">/ 5</span>
              </div>
              <div className="mt-2"><RatingStars value={average} /></div>
              <p className="text-sm text-[#6B7280] mt-2" data-testid="rating-total">{ratingTotal} parent rating{ratingTotal === 1 ? '' : 's'}</p>
              <div className="mt-4 space-y-1.5" data-testid="rating-distribution">
                {[5, 4, 3, 2, 1].map(starVal => {
                  const count = distribution[starVal] || 0;
                  const pct = ratingTotal ? Math.round((count / ratingTotal) * 100) : 0;
                  return (
                    <div key={starVal} className="flex items-center gap-2" data-testid={`rating-bar-${starVal}`}>
                      <span className="text-[11px] text-[#6B7280] w-3">{starVal}</span>
                      <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                      <div className="flex-1 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-[#9CA3AF] w-5 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-3 space-y-2.5" data-testid="latest-feedback">
              {feedback.slice(0, 3).map(f => (
                <div key={f.id} className="rounded-2xl border border-[#EFF0F2] p-4" data-testid={`feedback-item-${f.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <RatingStars value={f.feedback_rating} size="h-3.5 w-3.5" />
                      {f.feedback_helpful !== null && f.feedback_helpful !== undefined && (
                        <Badge className={`border-0 text-[9px] ${f.feedback_helpful ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                          {f.feedback_helpful ? 'Helpful' : 'Not helpful'}
                        </Badge>
                      )}
                    </div>
                    <span className="text-[10px] text-[#9CA3AF]">{f.feedback_submitted_at ? formatDate(f.feedback_submitted_at) : ''}</span>
                  </div>
                  {f.feedback_comment && <p className="text-sm text-[#4B5563] leading-relaxed mb-2">"{f.feedback_comment}"</p>}
                  <button onClick={() => navigate(`/sessions/${f.id}`)} className="text-[11px] font-semibold text-[#F97D6A] hover:text-[#F06651]" data-testid={`feedback-link-${f.id}`}>
                    {f.career_name}{f.child_name ? ` · ${f.child_name}` : ''} →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* My availability */}
      <section className="mb-10" data-testid="my-availability">
        <SectionHeading title="My availability" />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-[#EFF0F2] p-5" data-testid="online-availability">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-blue-500" />
              <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Online availability</h3>
            </div>
            {onlineSlots.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] mb-4">No weekly slots configured yet.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {onlineSlots.map((day, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-[#1F2937] w-10">{day.day?.slice(0, 3)}</span>
                    <div className="flex flex-wrap gap-1">
                      {(day.time_ranges || []).map((tr, j) => (
                        <span key={j} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-[10px]">{fmtTime(tr.start)} – {fmtTime(tr.end)}{tr.duration_minutes ? ` · ${tr.duration_minutes}m` : ''}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/mentor/profile')} className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB]" data-testid="edit-availability-btn">
              <Pencil className="h-3 w-3 mr-1.5" /> Edit Availability
            </Button>
          </div>

          <div className="rounded-2xl border border-[#EFF0F2] p-5" data-testid="offline-availability">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-emerald-500" />
              <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">Workplace Visit Offerings</h3>
            </div>
            {offlineCaps.length === 0 ? (
              <p className="text-xs text-[#9CA3AF] mb-4">No workplace opportunities published yet.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {offlineCaps.slice(0, 3).map((cap, i) => {
                  const careerName = (profile.mapped_careers || []).find(c => c.slug === cap.career_slug)?.name || cap.career_slug;
                  return (
                    <div key={i} className="text-xs">
                      <p className="font-semibold text-[#1F2937]">{careerName}</p>
                      <p className="text-[11px] text-[#6B7280]">{cap.workplace_name} · {cap.city}{cap.date ? ` · ${formatDate(cap.date)}` : ''}</p>
                    </div>
                  );
                })}
                {offlineCaps.length > 3 && <p className="text-[11px] text-[#9CA3AF]">+{offlineCaps.length - 3} more</p>}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/mentor/experiences?tab=offline')} className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB]" data-testid="manage-offline-btn">
              <Plus className="h-3 w-3 mr-1.5" /> Manage Visits
            </Button>
          </div>
        </div>
      </section>

      {/* How families see you */}
      <section className="mb-10" data-testid="profile-preview">
        <SectionHeading title="How families see you" action="Edit profile" onAction={() => navigate('/mentor/profile')} actionTestId="edit-profile-preview" />
        <div className="rounded-2xl border border-[#EFF0F2] p-5 flex items-start gap-4">
          {profile.photo_url ? (
            <img src={profile.photo_url} alt={user?.full_name || 'Mentor'} className="h-14 w-14 rounded-full object-cover shrink-0" data-testid="preview-photo" />
          ) : (
            <span className="h-14 w-14 rounded-full bg-[#FFF0ED] text-[#F97D6A] text-lg font-bold flex items-center justify-center shrink-0" data-testid="preview-initial">
              {(user?.full_name || 'M').replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, '').charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[#1F2937] font-['Outfit']" data-testid="preview-name">{user?.full_name}</p>
            <p className="text-sm text-[#6B7280] mt-0.5">
              {profile.profession_title}{profile.company_name ? ` · ${profile.company_name}` : ''}
              {profile.years_of_experience ? ` · ${profile.years_of_experience} yrs` : ''}
            </p>
            {(profile.mapped_careers || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="preview-careers">
                {profile.mapped_careers.map(c => (
                  <Badge key={c.slug} className="border-0 bg-[#F3F4F6] text-[#4B5563] text-[10px]">{c.name}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-[#9CA3AF] mt-2">No careers mapped yet — families search by career, so add at least one.</p>
            )}
          </div>
        </div>
      </section>

      <CompleteExperienceDialog exp={completing} open={!!completing} onOpenChange={(o) => { if (!o) setCompleting(null); }} onDone={() => { setCompleting(null); refresh(); }} />
      <DeclarationDialog open={showDeclaration} onOpenChange={setShowDeclaration} version={declarationInfo?.declaration_version} onDone={refresh} />
    </MentorWorkspace>
  );
}
